import { useMemo } from 'react';

import { SongPreview } from '~/interfaces';
import clearString from '~/modules/utils/clear-string';

/** A genre needs at least this many songs to be worth a filter of its own. */
const MIN_SONGS = 8;

/**
 * Values that appear in the genre field but name no genre. `none` alone covers 207 songs in the
 * shipped catalogue, and would otherwise sit third in the list.
 */
const PLACEHOLDERS = new Set(['none', 'unknown', 'other', 'n/a', '-']);

/** Whether a genre field holds one of the values that name no genre. */
export const isPlaceholderGenre = (genre: string) => PLACEHOLDERS.has(genre.trim().toLowerCase());

/**
 * The one genre behind several spellings of it.
 *
 * Song files are written by hand by whoever made them, so the same genre arrives spelled every way
 * it can be: 58 songs say K-Pop and 43 say Kpop, 22 say J-Pop and 17 say Jpop. Keyed by name those
 * are two filters holding half the songs each, and picking either one hides the rest - which reads
 * as songs having gone missing.
 */
export const genreKey = (genre: string) => clearString(genre);

/**
 * Genres present in the list, commonest first. Only about two thirds of the catalogue carries a
 * genre, so this describes what can actually be filtered rather than a fixed set of names.
 *
 * Spellings are counted together and the commonest one is the name shown, so the bar offers K-Pop
 * once for all 101 of them rather than twice for 58 and 43.
 */
export const useGenreList = (list: Partial<Pick<SongPreview, 'genre'>>[], minSongs = MIN_SONGS) =>
  useMemo(() => {
    const genres: Record<string, { spellings: Record<string, number>; count: number }> = {};

    for (const song of list) {
      const genre = song.genre?.trim();
      if (!genre) continue;
      if (isPlaceholderGenre(genre)) continue;

      const key = genreKey(genre);
      if (!key) continue;

      genres[key] ??= { spellings: {}, count: 0 };
      genres[key].spellings[genre] = (genres[key].spellings[genre] ?? 0) + 1;
      genres[key].count++;
    }

    return Object.values(genres)
      .filter((genre) => genre.count >= minSongs)
      .map(({ spellings, count }) => ({
        name: Object.entries(spellings).sort(([, a], [, b]) => b - a)[0][0],
        count,
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [list, minSongs]);
